/**
 * Centralized analytics event catalog for Plausible.
 *
 * IMPORTANT: This is for Plausible Community Edition (self-hosted).
 * Custom properties work automatically - no dashboard configuration needed!
 *
 * All events are defined here for consistency and autocomplete.
 * Properties are flexible - use StandardProps constants for common properties.
 */

/**
 * Standard custom property keys used across the application.
 *
 * Use these constants to ensure consistency across all events.
 * Plausible CE automatically captures any properties sent - no pre-configuration needed.
 *
 * Property naming convention: snake_case
 */
export const StandardProps = {
    // User journey timing
    TIME_TO_VERIFY: "time_to_verify", // e.g., "5m", "2h", "1d"
    DAYS_SINCE_SIGNUP: "days_since_signup", // e.g., 0, 1, 7, 30

    // User attributes & choices
    ROLE: "role", // "organizer" | "competitor" | "both" | "undefined"
    METHOD: "method", // "email" | "myself" | "someone_else"

    // Action details
    CONSENT_TYPE: "consent_type", // "sms" | "email"

    // Organizer segmentation
    ORGANIZER_TYPE: "organizer_type", // "first_time" | "returning"
} as const;

/**
 * Unified event catalog.
 *
 * Event naming convention: "[Area] PascalCaseAction"
 *
 * Used for both:
 * - Composable-tracked events: use useAnalytics() and reference names from this catalog
 * - CSS-class-tracked events (Twig/Vue templates): encode as plausible-event-name=[Area]+PascalCaseAction
 *   where + is a space — Plausible decodes it as "[Area] PascalCaseAction" in the dashboard
 *
 * CSS-class events in use:
 *   [Nav]+ContactUs              — contact link in navbar
 *   [Footer]+ContactUs           — contact link in footer
 *   [Welcome]+CreateEvent        — create event CTA on welcome page
 *   [Welcome]+ContactUs          — contact CTA on welcome page
 *   [HelpCenter]+CreateEvent     — create event CTA in help center articles
 *   [AlternativeToSmoothcomp]+ContactUs — contact CTA on comparison landing pages
 *   [PublicEvent]+RegisterToEvent — register button on public event page
 *   [Pricing]+CreateEvent        — create event CTA on pricing page (hero + bottom)
 *   [Pricing]+ContactUs          — contact CTA on pricing page
 *   [Organizer]+AddNewDraftEvent — add event button in organizer event list
 *   [Organizer]+CreateFirstEvent — create first event CTA for new organizers
 *   [Registration]+RegisterToEvent   — open registration button (personal events list)
 *   [Registration]+ConfirmCompetitors — confirm competitors button in registration flow
 *   [Payment]+PayNow             — pay now button in payment modal
 */
export const AnalyticsEvents = {
    // Signup Funnel
    Signup: {
        Submitted: "[Signup] Submitted",
    },

    // Account Activation (Email Verification)
    AccountActivation: {
        EmailResent: "[AccountActivation] EmailResent",
        EmailChanged: "[AccountActivation] EmailChanged",
    },

    // Completing Profile (Post-verification profile setup)
    CompletingProfile: {
        Started: "[CompletingProfile] Started",
        PersonalDataProvided: "[CompletingProfile] PersonalDataProvided",
        RoleSelected: "[CompletingProfile] RoleSelected",
        Completed: "[CompletingProfile] Completed",
    },

    // Organizer First Event Journey (Fresh/Unapproved Federation)
    Organizer: {
        FirstDraftCreated: "[Organizer] FirstDraftCreated",
        PublishAttempted: "[Organizer] PublishAttempted",
        ReviewFormCanceled: "[Organizer] ReviewFormCanceled",
        ReviewSubmitted: "[Organizer] ReviewSubmitted",
        ReviewSubmitFailed: "[Organizer] ReviewSubmitFailed",
        WizardStarted: "[Organizer] WizardStarted",
        WizardStep1Completed: "[Organizer] WizardStep1Completed",
        WizardStep2Completed: "[Organizer] WizardStep2Completed",
        WizardStep3Completed: "[Organizer] WizardStep3Completed",
        WizardStep4Completed: "[Organizer] WizardStep4Completed",
        WizardStep5Completed: "[Organizer] WizardStep5Completed",
        WizardStep5Skipped: "[Organizer] WizardStep5Skipped",
        WizardAbandoned: "[Organizer] WizardAbandoned",
        WizardSkipped: "[Organizer] WizardSkipped",
        OnboardingModalShown: "[Organizer] OnboardingModalShown",
        OnboardingTourStarted: "[Organizer] OnboardingTourStarted",
        OnboardingSkipped: "[Organizer] OnboardingSkipped",
        OnboardingTourCompleted: "[Organizer] OnboardingTourCompleted",
        OnboardingTourAbandoned: "[Organizer] OnboardingTourAbandoned",
    },

    // Global Setup Checklist banner (organizer admin pages)
    Checklist: {
        Expanded: "[Checklist] Expanded",
        ItemClicked: "[Checklist] ItemClicked",
        Dismissed: "[Checklist] Dismissed",
        AllComplete: "[Checklist] AllComplete",
    },
} as const;

// Type helper to extract all valid event names
export type AnalyticsEventName =
    | (typeof AnalyticsEvents.Signup)[keyof typeof AnalyticsEvents.Signup]
    | (typeof AnalyticsEvents.AccountActivation)[keyof typeof AnalyticsEvents.AccountActivation]
    | (typeof AnalyticsEvents.CompletingProfile)[keyof typeof AnalyticsEvents.CompletingProfile]
    | (typeof AnalyticsEvents.Organizer)[keyof typeof AnalyticsEvents.Organizer]
    | (typeof AnalyticsEvents.Checklist)[keyof typeof AnalyticsEvents.Checklist];

/**
 * Event properties type - flexible to allow any scalar values.
 * Use StandardProps constants for consistency.
 */
export type EventProperties = Record<string, string | number | boolean>;

/**
 * Plausible CE limits for custom properties.
 */
export const PLAUSIBLE_LIMITS = {
    MAX_PROPERTIES_PER_EVENT: 30,
    MAX_PROPERTY_NAME_LENGTH: 300,
    MAX_PROPERTY_VALUE_LENGTH: 2000,
} as const;

/**
 * Validate event properties against Plausible CE limits.
 *
 * @param props - Properties to validate
 * @returns true if valid, false otherwise (warnings logged to console)
 */
export function validateProperties(props: EventProperties): boolean {
    const propCount = Object.keys(props).length;

    if (propCount > PLAUSIBLE_LIMITS.MAX_PROPERTIES_PER_EVENT) {
        console.warn(
            `[Analytics] Too many properties (${propCount}), max is ${PLAUSIBLE_LIMITS.MAX_PROPERTIES_PER_EVENT}.`,
            "Properties:",
            props,
        );
        return false;
    }

    for (const [key, value] of Object.entries(props)) {
        if (key.length > PLAUSIBLE_LIMITS.MAX_PROPERTY_NAME_LENGTH) {
            console.warn(
                `[Analytics] Property name too long (${key.length} chars), max is ${PLAUSIBLE_LIMITS.MAX_PROPERTY_NAME_LENGTH}.`,
                "Property:",
                key,
            );
            return false;
        }

        const valueStr = String(value);
        if (valueStr.length > PLAUSIBLE_LIMITS.MAX_PROPERTY_VALUE_LENGTH) {
            console.warn(
                `[Analytics] Property value too long (${valueStr.length} chars), max is ${PLAUSIBLE_LIMITS.MAX_PROPERTY_VALUE_LENGTH}.`,
                "Property:",
                key,
                "Value:",
                valueStr.substring(0, 100) + "...",
            );
            return false;
        }
    }

    return true;
}
